import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import { format, subYears } from 'date-fns'

// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { formatCurrencyInt, maskCnpj } from '@core/utils/utils'

export const formatCompetence = value => {
  if (!value) return ''
  const [year, month] = String(value).split('-')
  return month ? `${month}/${year}` : value
}

export default function useWorkplacePayrollHistoryList() {
  // Use toast
  const toast = useToast()

  const now = new Date()
  const currentYear = format(now, 'yyyy')
  const currentMonth = format(now, 'MM')

  const refPayrollHistoryTable = ref(null)

  // Filters
  const filterYear = ref(currentYear)
  const filterMonth = ref(currentMonth)
  const institutionId = ref(null)
  const institutionOptions = ref([])
  const includeCanceled = ref(false)

  // Pagination
  const perPage = ref(50)
  const perPageOptions = [10, 25, 50, 100, 200]
  const currentPage = ref(1)
  const totalItems = ref(0)

  const yearOptions = (new Array(4)).fill(0).map((_, i) => {
    const year = format(subYears(now, i), 'yyyy')
    return { label: year, value: year }
  }).filter(({ value }) => value > '2020')

  const allMonthOptions = [
    { label: 'Janeiro', value: '01' },
    { label: 'Fevereiro', value: '02' },
    { label: 'Março', value: '03' },
    { label: 'Abril', value: '04' },
    { label: 'Maio', value: '05' },
    { label: 'Junho', value: '06' },
    { label: 'Julho', value: '07' },
    { label: 'Agosto', value: '08' },
    { label: 'Setembro', value: '09' },
    { label: 'Outubro', value: '10' },
    { label: 'Novembro', value: '11' },
    { label: 'Dezembro', value: '12' },
  ]

  const monthOptions = computed(() => (
    filterYear.value === currentYear
      ? allMonthOptions.filter(({ value }) => value <= currentMonth)
      : allMonthOptions
  ))

  watch(filterYear, value => {
    if (value === currentYear && filterMonth.value > currentMonth) {
      filterMonth.value = currentMonth
    }
  })

  const tableColumns = [
    { key: 'institution_name', label: 'Instituição' },
    { key: 'workplace_name', label: 'Unidade' },
    { key: 'payroll', label: 'Valor Folha', formatter: formatCurrencyInt },
    { key: 'actions', label: 'Ações' },
  ]

  const refetchData = () => {
    refPayrollHistoryTable.value.refresh()
  }

  watch(currentPage, () => {
    refetchData()
  })

  // Trocar o tamanho da página volta pra primeira, senão a página atual pode não existir mais
  watch(perPage, () => {
    if (currentPage.value !== 1) {
      currentPage.value = 1
      return
    }
    refetchData()
  })

  const search = () => {
    if (currentPage.value !== 1) {
      currentPage.value = 1
      return
    }
    refetchData()
  }

  const fetchItems = (ctx, callback) => {
    store
      .dispatch('app-reports/workplacePayrollHistory', {
        competence: `${filterYear.value}-${filterMonth.value}`,
        institution: institutionId.value ? institutionId.value.value : undefined,
        include_canceled: includeCanceled.value ? 1 : 0,
        per_page: perPage.value,
        page: currentPage.value,
      })
      .then(response => {
        const { data, meta } = response.data
        totalItems.value = meta.pagination.total
        callback(data)
      })
      .catch(() => {
        totalItems.value = 0
        callback([])
        toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao buscar',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const fetchInstitutions = () => {
    store.dispatch('app-reports/fetchInstitutions', { perPage: 99999999 })
      .then(response => {
        institutionOptions.value = response.data.data.map(inst => ({
          value: inst.id,
          label: `${inst.name} | ${maskCnpj(inst.cnpj)}`,
        }))
      })
  }

  return {
    fetchItems,
    fetchInstitutions,
    tableColumns,
    perPage,
    perPageOptions,
    currentPage,
    totalItems,
    refPayrollHistoryTable,
    refetchData,
    search,

    // Filters
    filterYear,
    filterMonth,
    yearOptions,
    monthOptions,
    institutionId,
    institutionOptions,
    includeCanceled,

    // UI
    formatCompetence,
  }
}
